/**
 * CSV export helpers for the Old Gold Reconciliation report.
 *
 * Design choices
 *   • Long-format (one row per record × category) — pivots trivially in Excel.
 *   • Dust / Others rows are emitted only when there's actual data (expected
 *     or actual > 0). Insert categories always emit so the pcs baseline is
 *     preserved.
 *   • Numbers use `.` decimals and no thousands separators for locale safety.
 *   • Filename embeds tab + local date so multiple exports don't clobber.
 */

const CATS = ['diamond', 'stone', 'beads_pearl', 'dust', 'others'];
const CAT_LABEL = {
  diamond: 'Diamond',
  stone: 'Stone',
  beads_pearl: 'Beads/Pearl',
  dust: 'Dust',
  others: 'Others',
};
const WEIGHT_ONLY = new Set(['dust', 'others']);

const escapeCsv = (v) => {
  if (v === null || v === undefined) return '';
  const s = String(v);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};

const fmtNum = (n, digits = 3) => {
  const v = Number(n);
  if (!Number.isFinite(v) || v === 0) return '';
  return v.toFixed(digits);
};

const fmtInt = (n) => {
  const v = Number(n);
  if (!Number.isFinite(v) || v === 0) return '';
  return String(v);
};

// Emit a category row only when it carries information.
const catHasData = (cat, sub) => {
  if (!sub) return false;
  if (WEIGHT_ONLY.has(cat)) {
    return (sub.expected_wt || 0) > 0 || (sub.actual_wt || 0) > 0;
  }
  return true; // insert categories always show (0/0 is meaningful)
};

const buildRows = (rows, tab) => {
  const header =
    tab === 'pieces'
      ? [
          'Serial',
          'Branch',
          'Lot No',
          'Status',
          'Gross Wt (g)',
          'Purity %',
          'Confirmed By',
          'Confirmed At',
          'Category',
          'Expected Pcs',
          'Actual Pcs',
          'Diff Pcs',
          'Expected Wt (g)',
          'Actual Wt (g)',
          'Diff Wt (g)',
        ]
      : tab === 'lots'
      ? [
          'Lot No',
          'Assigned To',
          'Lot Status',
          'Pieces Broken',
          'Verdict',
          'Total Short (pcs)',
          'Total Extra (pcs)',
          'Category',
          'Expected Pcs',
          'Actual Pcs',
          'Diff Pcs',
          'Expected Wt (g)',
          'Actual Wt (g)',
          'Diff Wt (g)',
        ]
      : [
          'Branch',
          'Verdict',
          'Pieces Broken',
          'Total Short (pcs)',
          'Total Extra (pcs)',
          'Category',
          'Expected Pcs',
          'Actual Pcs',
          'Diff Pcs',
          'Expected Wt (g)',
          'Actual Wt (g)',
          'Diff Wt (g)',
        ];

  const out = [header];
  for (const r of rows) {
    for (const cat of CATS) {
      const sub = r.categories?.[cat];
      if (!catHasData(cat, sub)) continue;
      const catCols = [
        CAT_LABEL[cat],
        WEIGHT_ONLY.has(cat) ? '' : fmtInt(sub.expected),
        WEIGHT_ONLY.has(cat) ? '' : fmtInt(sub.actual),
        WEIGHT_ONLY.has(cat) ? '' : fmtInt(sub.diff),
        fmtNum(sub.expected_wt),
        fmtNum(sub.actual_wt),
        fmtNum(sub.diff_wt),
      ];
      if (tab === 'pieces') {
        out.push([
          r.serial_no,
          r.branch,
          r.lot_no,
          r.status,
          fmtNum(r.gross_weight),
          fmtNum(r.expected_purity, 2),
          r.confirmed_by_name || '',
          r.confirmed_at || '',
          ...catCols,
        ]);
      } else if (tab === 'lots') {
        out.push([
          r.lot_no,
          r.assigned_to_name || '',
          r.status,
          fmtInt(r.pieces_broken),
          r.verdict,
          fmtInt(r.total_short_pcs),
          fmtInt(r.total_extra_pcs),
          ...catCols,
        ]);
      } else {
        out.push([
          r.branch,
          r.verdict,
          fmtInt(r.pieces_broken),
          fmtInt(r.total_short_pcs),
          fmtInt(r.total_extra_pcs),
          ...catCols,
        ]);
      }
    }
  }
  return out;
};

const todayStamp = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/**
 * Trigger a browser download of the current reconciliation data as CSV.
 * `tab` is one of `pieces` | `lots` | `branches`.
 * Adds a UTF-8 BOM so Excel opens with correct diacritics.
 */
export function exportReconciliationCsv(rows, tab) {
  const grid = buildRows(rows || [], tab);
  const csv = grid.map((r) => r.map(escapeCsv).join(',')).join('\r\n');
  const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `oldgold-reconciliation-${tab}-${todayStamp()}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
